import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFeatureEntitlements } from "@frontegg/react";
import Button from "@mui/material/Button";
import { PageContainer } from "@outpave/ds";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import SubscriptionsTable, {
  SubscriptionFilterCriteriaProps,
} from "components/admin/subscriptions/SubscriptionsTable";
import SubscriptionDetailsDrawer from "components/admin/subscriptions/SubscriptionDetailsDrawer";
import CreateEditPaymentLinkDrawer from "components/admin/paymentLinks/CreateEditPaymentLinkDrawer";
import {
  LISTABLE_SUBSCRIPTION_STATUSES,
  SubscriptionStatus,
} from "enums/Subscription";
import { PaymentLinkType } from "enums/PaymentLink";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import useGetParamFromUrl from "hooks/useGetParamFromUrl";
import { useDrawerRecord } from "hooks/useDrawerRecord";
import Role from "enums/Role";

const Subscriptions: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdminOrHigher = useCheckUserRole(Role.Admin);
  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });
  const { isEntitled: featurePaymentLinks } = useFeatureEntitlements(
    "feature_payment_links",
  );

  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const search = useDebouncedSearch(setSearchTerm);
  const [refreshIndex, setRefreshIndex] = useState(0);

  const subscriptionDrawer = useDrawerRecord<string>();
  const newLinkDrawer = useDrawerRecord<PaymentLinkType>();

  const [filterCriteria, setFilterCriteria] =
    useState<SubscriptionFilterCriteriaProps>({
      status: [SubscriptionStatus.ACTIVE, SubscriptionStatus.PAST_DUE],
      searchTerm: "",
    });

  const tabConfigurations = [
    {
      label: t("Active"),
      status: [
        SubscriptionStatus.ACTIVE,
        SubscriptionStatus.PAST_DUE,
        SubscriptionStatus.INCOMPLETE,
      ],
    },
    { label: t("Canceled"), status: [SubscriptionStatus.CANCELED] },
    { label: t("Show All"), status: LISTABLE_SUBSCRIPTION_STATUSES },
  ];

  useEffect(() => {
    setFilterCriteria({
      status: tabConfigurations[activeTab].status,
      searchTerm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm]);

  const handleRefresh = () => setRefreshIndex((previous) => previous + 1);

  const handleViewDetails = (subscriptionId: string) =>
    subscriptionDrawer.show(subscriptionId);

  const resetParam = useGetParamFromUrl("id", handleViewDetails);

  const handleCloseDrawer = (refresh: boolean = false) => {
    if (refresh) handleRefresh();
    resetParam();
    subscriptionDrawer.close();
  };

  const handleCloseNewLink = (_refresh?: boolean, createdId?: string) => {
    if (createdId) {
      navigate(`/admin/payment-links?id=${createdId}`);
      return;
    }

    newLinkDrawer.close();
  };

  return (
    <PageContainer
      title={t("Subscriptions")}
      primaryAction={
        featurePaymentLinks && !isAuditor ? (
          <Button
            onClick={() => newLinkDrawer.show(PaymentLinkType.SUBSCRIPTION)}
          >
            {t("New Subscription")}
          </Button>
        ) : undefined
      }
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={search}
    >
      <>
        <SubscriptionsTable
          filterCriteria={filterCriteria}
          refreshIndex={refreshIndex}
          readOnly={!isAdminOrHigher}
          onViewDetails={handleViewDetails}
          onRefresh={handleRefresh}
          emptyHint={
            featurePaymentLinks ? t("subscription.EmptyHint") : undefined
          }
        />

        {subscriptionDrawer.record && (
          <SubscriptionDetailsDrawer
            open={subscriptionDrawer.open}
            subscriptionId={subscriptionDrawer.record}
            readOnly={!isAdminOrHigher}
            onClose={handleCloseDrawer}
            onAfterClose={subscriptionDrawer.afterClose}
          />
        )}

        {newLinkDrawer.record && (
          <CreateEditPaymentLinkDrawer
            open={newLinkDrawer.open}
            defaultLinkType={newLinkDrawer.record}
            onClose={handleCloseNewLink}
            onAfterClose={newLinkDrawer.afterClose}
          />
        )}
      </>
    </PageContainer>
  );
};

export default Subscriptions;
